import { useMemo } from "react";
import { useParams } from "next/navigation";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  Content,
  ContentCategoryEnum,
  ContentTypeEnum,
  ContentPaginationResponse,
} from "../features/api/openData";
import { I18nUtil } from "../utils/i18n";
import { useNewsRankingThemeRows } from "./useNewsRankingThemeRows";
import {
  attachThemeRankingToContents,
  type ContentWithThemeRanking,
} from "./useHotNews.helper";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { QUERY_KEYS } from "@config/constant";
import { withRankingContentsTimeWindow } from "@utils/rankingContentsTimeWindow";

export type { ContentWithThemeRanking } from "./useHotNews.helper";

export interface UseHotNewsParams {
  locale?: string;
  limit?: number;
  offset?: number;
  themes?: string[];
  themeIds?: string[];
  category?: ContentCategoryEnum;
  symbols?: string[];
  /** 股票市场，仅 type 为 stock 时生效（US/HK） */
  market?: string;
}

export interface UseHotNewsOptions {
  initialParams?: UseHotNewsParams;
  enabled?: boolean;
  queryKey?: string[];
  /** 初始数据，通常来自服务端渲染 */
  initialData?: {
    data: Content[];
    total: number;
  };
}

const PAGE_SIZE = 20;

/**
 * 用于获取热门新闻的自定义Hook - 使用 TanStack Query useInfiniteQuery
 * @param options Hook选项
 * @returns 新闻数据和相关状态
 */
export function useHotNews(options: UseHotNewsOptions = {}) {
  const {
    initialParams = {},
    enabled = true,
    initialData,
    queryKey: customQueryKey,
  } = options;
  const { openDataApi } = useOspApiClientProvider();
  const { locale } = useParams<{ locale?: string }>();

  const rankingLocale = locale ?? "en";

  const { data: rankingThemeRows } = useNewsRankingThemeRows({
    locale: rankingLocale,
    enabled: enabled && !!openDataApi,
  });

  // 跟踪是否已经手动触发过查询（用于禁用首次自动查询的场景）
  // const [hasManuallyFetched, setHasManuallyFetched] = useState(false);

  // 设置默认参数 - 使用 useMemo 避免重新渲染时重新创建对象
  const defaultParams = useMemo(
    (): UseHotNewsParams => ({
      locale: "en",
      limit: PAGE_SIZE,
      offset: 0,
      category: ContentCategoryEnum.CRYPTO,
      themes: [],
      themeIds: [],
      symbols: [],
      ...initialParams,
    }),
    [initialParams],
  );

  // 生成查询键，确保参数变化时重新查询
  const queryKey = useMemo(
    () =>
      customQueryKey
        ? [QUERY_KEYS.HOT_NEWS_QUERY_KEY, ...customQueryKey]
        : [
            QUERY_KEYS.HOT_NEWS_QUERY_KEY,
            defaultParams,
            defaultParams.themes,
            defaultParams.themeIds,
            defaultParams.symbols,
          ],
    [defaultParams, customQueryKey],
  );

  const {
    data,
    isLoading,
    isFetching,
    isPending,
    error,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetchingNextPage,
  } = useInfiniteQuery<
    ContentPaginationResponse,
    Error,
    Content[],
    any,
    UseHotNewsParams
  >({
    queryKey,
    enabled: enabled && !!openDataApi,
    // stock 每股会存在不请求news的情况，数据展示不正确
    // staleTime: 1000 * 60,
    initialData: initialData && {
      pages: [
        {
          code: 0,
          msg: "success",
          data: {
            limit: defaultParams.limit || PAGE_SIZE,
            total: initialData?.total,
            offset: 0,
            rows: initialData?.data,
          },
        },
      ],
      pageParams: [defaultParams],
    },
    queryFn: async ({ pageParam }) => {
      if (!openDataApi) {
        throw new Error("openDataApi is not defined");
      }
      const params = pageParam || defaultParams;
      const queryParams = withRankingContentsTimeWindow({
        limit: params.limit || PAGE_SIZE,
        offset: params.offset || 0,
        type: ContentTypeEnum.INSIGHT,
        category: params.category || ContentCategoryEnum.CRYPTO,
        market: params.market,
        language: I18nUtil.getBackgroundLanguageKey(locale as string),
        themes: params.themes,
        themeIds: params.themeIds,
        symbols: params.symbols,
      });
      const response =
        await openDataApi.contents.getRankingContents(queryParams);
      if (!response.data) {
        throw new Error("获取新闻数据失败");
      }

      return response.data;
    },
    initialPageParam: defaultParams,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.data?.total) {
        return undefined;
      }

      if (allPages.length === 0) {
        return { ...defaultParams, offset: 0 };
      }
      if (
        allPages.length * (lastPage?.data?.limit ?? PAGE_SIZE) >=
        (lastPage?.data?.total ?? 0)
      ) {
        return undefined;
      }

      return {
        ...defaultParams,
        offset: allPages.length * (lastPage?.data?.limit ?? PAGE_SIZE),
      };
    },
    select: (data) => {
      // 将所有页面的数据平铺成一个数组
      const allNewsItems =
        data?.pages?.flatMap((page) => page?.data?.rows || []) || [];
      return allNewsItems;
    },
  });

  const dataWithThemeRanking = useMemo((): ContentWithThemeRanking[] => {
    return attachThemeRankingToContents(data ?? [], rankingThemeRows);
  }, [data, rankingThemeRows]);

  // 包装 fetchNextPage，在禁用首次查询的场景下处理首次手动触发
  // const wrappedFetchNextPage = useCallback(async () => {
  //   if (useServerData && !hasManuallyFetched) {
  //     log.debug("[useHotNews] 🔄 首次手动触发查询，启用后续查询");
  //     setHasManuallyFetched(true);
  //     // 等待下一个 tick，让 enabled 状态更新后再调用 fetchNextPage
  //     await new Promise((resolve) => setTimeout(resolve, 0));
  //   }
  //   log.debug("[useHotNews] 📄 fetchNextPage");
  //   return fetchNextPage();
  // }, [useServerData, hasManuallyFetched, fetchNextPage]);

  return {
    data: dataWithThemeRanking,
    isLoading,
    isFetching,
    isPending,
    error,
    fetchNextPage,
    hasNextPage: hasNextPage || false,
    refetch,
    isFetchingNextPage,
  };
}

/**
 * 使用示例：
 *
 * function NewsComponent() {
 *   const {
 *     data,
 *     isLoading,
 *     isFetching,
 *     error,
 *     fetchNextPage,
 *     hasNextPage,
 *     refetch
 *   } = useHotNews({
 *     initialParams: {
 *       locale: 'en',
 *       pageSize: 5
 *     }
 *   });
 *
 *   if (isLoading && data.length === 0) {
 *     return <div>Loading...</div>;
 *   }
 *
 *   if (error) {
 *     return (
 *       <div>
 *         <p>Error: {error.message}</p>
 *         <button onClick={() => refetch()}>重试</button>
 *       </div>
 *     );
 *   }
 *
 *   return (
 *     <div>
 *       {data.map(item => (
 *         <div key={item.id}>
 *           <h3>{item.Title}</h3>
 *           <p>{item.Content}</p>
 *         </div>
 *       ))}
 *       {hasNextPage && (
 *         <button onClick={() => fetchNextPage()} disabled={isFetching}>
 *           {isFetching ? '加载中...' : '加载更多'}
 *         </button>
 *       )}
 *     </div>
 *   );
 * }
 *
 * // 简化版本使用示例：
 * function SimpleNewsComponent() {
 *   const { data, isLoading, error } = useLatestNews('zh');
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return (
 *     <div>
 *       {data.map(item => (
 *         <div key={item.id}>{item.Title}</div>
 *       ))}
 *     </div>
 *   );
 * }
 *
 * // 新闻详情使用示例：
 * function NewsDetailComponent({ documentId }: { documentId: string }) {
 *   const { data, isLoading, error, refetch } = useNewsDetail({
 *     documentId,
 *     locale: 'en'
 *   });
 *
 *   if (isLoading) return <div>Loading news detail...</div>;
 *   if (error) return (
 *     <div>
 *       <p>Error: {error.message}</p>
 *       <button onClick={() => refetch()}>重试</button>
 *     </div>
 *   );
 *   if (!data) return <div>News not found</div>;
 *
 *   return (
 *     <article>
 *       <h1>{data.Title}</h1>
 *       <img src={data.HeadImg} alt={data.Seo?.metaAlt} />
 *       <p>作者: {data.Author}</p>
 *       <time>{new Date(data.publishedAt).toLocaleDateString()}</time>
 *       <div dangerouslySetInnerHTML={{ __html: data.Content }} />
 *
 *       {data.Tokens && data.Tokens.length > 0 && (
 *         <div>
 *           <h3>相关代币:</h3>
 *           {data.Tokens.map(token => (
 *             <span key={token.id}>{token.Name}</span>
 *           ))}
 *         </div>
 *       )}
 *
 *       {data.References && data.References.length > 0 && (
 *         <div>
 *           <h3>参考链接:</h3>
 *           {data.References.map(ref => (
 *             <a key={ref.id} href={ref.Url} target="_blank" rel="noopener noreferrer">
 *               {ref.Name}
 *             </a>
 *           ))}
 *         </div>
 *       )}
 *     </article>
 *   );
 * }
 */
